<template>
  <div class="upgrade-plan-page py-8 px-4 px-md-6">

    <v-col cols="12" align-self="center" class="header-container mb-12">
      <div class="d-flex align-start flex-column">
        <div class="d-flex align-center">
          <v-img class="mr-2" :src="require('@/assets/icons/admin+/workspace_premium.svg')" width="24" height="24"
            max-width="24" contain />
          <span class="font-roboto font-weight-medium text-h5">{{ headerTitle }}</span>
        </div>
      </div>
    </v-col>


    <div class="mx-auto" style="max-width: 960px">

      <template v-if="showSubscriptionDetails">
        <h1 class="font-roboto text-h4 text-md-h3 font-weight-regular text-center mb-3 plusTextPrimary--text">
          {{ $t("message.admin_plus_upgrade_select_plan_title") }}
        </h1>
        <p class="font-roboto text-body-1 text-center mb-8 plusTextSecondary--text">
          {{ planSubtitle }}
        </p>

        <div class="d-flex justify-center mb-4 ">
          <div class="billing-pill d-inline-flex align-center pa-2 rounded-pill">
            <v-btn elevation="1"
              :class="['billing-pill-btn font-roboto text-body-2 font-weight-medium rounded-pill', { 'billing-pill-btn--active': !billingYearly }]"
              @click="billingYearly = false">
              {{ $t("message.admin_plus_upgrade_billing_monthly") }}
            </v-btn>
            <v-btn elevation="1"
              :class="['billing-pill-btn font-roboto text-body-2 font-weight-medium rounded-pill', { 'billing-pill-btn--active': billingYearly }]"
              @click="billingYearly = true">
              <span>{{ $t("message.admin_plus_upgrade_billing_yearly") }}</span>

              <v-chip class="ml-2 px-1 rounded-pill" :color="billingYearly ? 'plusGreen' : 'plusGreenAccent3'"
                :text-color="billingYearly ? 'plusGreyDarken4' : 'plusGreenDarken4'" label x-small>
                {{ $t("message.admin_plus_upgrade_discount_badge") }}
              </v-chip>
            </v-btn>
          </div>
        </div>

        <v-sheet rounded class="d-flex align-center pa-4 mb-8 justify-center sheet-banner">
          <v-icon color="primary" class="mr-3 mt-1 align-self-center">mdi-tag-multiple-outline</v-icon>
          <div class="d-flex flex-column">
            <span class="font-roboto font-weight-medium plusTextPrimary--text">
              {{ $t("message.admin_plus_upgrade_banner_title") }}
            </span>
            <span class="font-roboto text-body-2 plusTextSecondary--text">
              {{ " " }}{{ $t("message.admin_plus_upgrade_banner_subtitle") }}
            </span>
          </div>
        </v-sheet>
      </template>

      <template v-else>
        <v-progress-linear v-if="subscriptionFetchLoading" indeterminate color="primary" class="mb-2 rounded" />
        <v-alert v-if="subscriptionFetchError" type="error" dense outlined class="mb-4" border="left">
          {{ $t("message.admin_plus_subscription_load_error") }}
        </v-alert>


        <v-card outlined class="pa-8 font-roboto" color="plusSurface">
          <p class="font-roboto text-h6 mb-8 plusTextPrimary--text">
            Current Plan
          </p>

          <div outlined class="mb-8 rounded-lg subscription-current-card" color="plusSurface">

            <v-sheet dark tile color="plusGreyDarken3" class="rounded-t-lg pa-5 subscription-plan-bar">
              <div class="font-roboto text-body-1 plusTextOnSecondary--text">
                <span class="font-roboto font-weight-bold text-h5">{{ subscriptionViewModel.planName }}</span>
                <span v-if="subscriptionViewModel.barRest"> {{ subscriptionViewModel.barRest }}</span>
              </div>
            </v-sheet>
            <v-card-text class="pt-6">
              <v-row v-for="row in subscriptionDetailRows" :key="row.key" class="subscription-detail-row" dense>
                <v-col cols="12" sm="4" class="font-roboto text-body-2 plusTextSecondary--text py-1">
                  {{ row.label }}
                </v-col>
                <v-col cols="12" sm="8" class="font-roboto text-body-2 plusTextPrimary--text py-1">
                  <template v-if="row.paymentBrand">
                    <span class="subscription-payment-brand mr-2">{{ row.paymentBrand }}</span>
                    <span>{{ row.value }}</span>
                  </template>
                  <template v-else>
                    {{ row.value }}
                  </template>
                </v-col>
              </v-row>


            </v-card-text>
          </div>

          <div outlined class="rounded-lg subscription-invoices-card" color="plusSurface">
            <v-card-title class="font-roboto font-weight-bold text-subtitle-1">
              {{ $t("message.admin_plus_subscription_invoices") }}
            </v-card-title>
            <v-simple-table class="subscription-invoices-table">
              <thead>
                <tr>
                  <th class="font-roboto text-caption font-weight-medium">{{
                    $t("message.admin_plus_subscription_col_date")
                  }}</th>
                  <th class="font-roboto text-caption font-weight-medium">{{
                    $t("message.admin_plus_subscription_col_invoice") }}</th>
                  <th class="font-roboto text-caption font-weight-medium">{{
                    $t("message.admin_plus_subscription_col_amount") }}</th>
                  <th class="font-roboto text-caption font-weight-medium">{{
                    $t("message.admin_plus_subscription_col_status") }}</th>
                  <th class="font-roboto text-caption font-weight-medium">{{
                    $t("message.admin_plus_subscription_col_actions") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(inv, idx) in subscriptionViewModel.invoices" :key="inv.idKey || idx">
                  <td class="font-roboto text-body-2">{{ inv.dateFormatted }}</td>
                  <td class="font-roboto text-body-2">{{ inv.numberDisplay }}</td>
                  <td class="font-roboto text-body-2">{{ inv.amountFormatted }}</td>
                  <td>
                    <v-chip v-if="inv.statusChip" :color="inv.statusChipColor" label small class="font-roboto">
                      {{ inv.statusChip }}
                    </v-chip>
                    <span v-else class="font-roboto text-body-2">{{ inv.statusFormatted }}</span>
                  </td>
                  <td>
                    <v-menu v-if="inv.downloadChoices && inv.downloadChoices.length > 1" offset-y>
                      <template #activator="{ on, attrs }">
                        <v-btn small outlined color="plusTextPrimary" class="font-roboto" v-bind="attrs" v-on="on">
                          {{ $t("message.admin_plus_subscription_download") }}
                          <v-icon right small>mdi-chevron-down</v-icon>
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item v-for="(c, cidx) in inv.downloadChoices" :key="cidx"
                          @click="openInvoiceUrl(c.url)">
                          <v-list-item-title>{{ c.label }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                    <v-btn v-else icon x-small :disabled="!inv.downloadUrl"
                      :aria-label="$t('message.admin_plus_subscription_download')" @click="onInvoiceDownload(inv)">
                      <v-icon small color="plusTextPrimary">mdi-open-in-new</v-icon>
                    </v-btn>
                  </td>
                </tr>
                <tr v-if="!subscriptionViewModel.invoices.length">
                  <td colspan="5" class="font-roboto text-body-2 text-center py-6 plusTextSecondary--text">
                    {{ $t("message.admin_plus_subscription_no_invoices") }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <!-- <v-card-actions class="justify-center pb-4">
              <v-btn text color="primary" class="font-roboto" @click="onViewAllInvoices">
                {{ $t("message.admin_plus_subscription_view_all_invoices") }}
                <v-icon right small>mdi-chevron-right</v-icon>
              </v-btn>
            </v-card-actions> -->
          </div>

        </v-card>
      </template>

      <v-row v-if="showSubscriptionDetails">
        <v-col cols="12" md="6">
          <v-card class="plan-card plan-card--black fill-height d-flex flex-column px-13 py-10" dark flat
            color="plusCardBlack">

            <div class="plan-badges">
              <v-chip class="font-roboto font-weight-bold rounded-l-md rounded-r-0" color="plusGreyLighten5"
                text-color="plusGreyDarken4" label small>
                {{ $t("message.admin_plus_upgrade_recommended") }}
              </v-chip>
              <v-chip class="font-roboto font-weight-bold rounded-l-0 rounded-r-md" color="plusGreenAccent3"
                text-color="plusGreyDarken4" label small>
                {{ $t("message.admin_plus_upgrade_save_amount") }}
              </v-chip>
            </div>

            <v-card-text class="pt-10 pb-2 flex-grow-1 d-flex flex-column">

              <div class="font-roboto text-h4 font-weight-bold mb-1 plusTextOnSecondary--text">
                {{ $t("message.admin_plus_upgrade_plan_black") }}
              </div>
              <div class="d-flex align-baseline flex-wrap mb-1 plusTextOnSecondary--text ">
                <span class="font-roboto text-h3 font-weight-bold">${{ billingYearly ? "529.00" : displayPriceBlack
                  }}</span>
              </div>
              <div class="font-roboto text-body-2 plusGreyLighten2--text mb-3">
                {{ blackBillingNote }}
              </div>
              <div class="font-roboto text-body-2 plusGreenAccent3--text mb-4 d-flex align-center">
                <v-icon small color="plusGreenAccent3" class="mr-1">mdi-tag-multiple-outline</v-icon>
                {{ $t("message.admin_plus_upgrade_save_annual_line") }}
              </div>
              <ul class="feature-list pl-0 mb-6">
                <li v-for="key in featureKeys" :key="key" class="d-flex align-center justify-start mb-2">
                  <v-icon color="plusGreenAccent3" size="20" class="mr-2 mt-0">mdi-check-circle</v-icon>
                  <span class="font-roboto text-body-1 plusTextOnSecondary--text">{{ $t(key) }}</span>
                </li>
              </ul>
              <v-btn block x-large depressed color="plusGrey40"
                class="font-roboto font-weight-medium cta-black-plan text-uppercase mt-auto"
                :disabled="checkoutDisabled" :loading="checkoutLoading" @click="onSelectPlan('black')">
                {{ $t("message.admin_plus_upgrade_cta_black") }}
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="plan-card plan-card--coral fill-height d-flex flex-column px-13 py-10" outlined
            color="plusSurface">
            <v-card-text class="pt-8 pb-2 flex-grow-1 d-flex flex-column">
              <div class="font-roboto text-h4 font-weight-bold plusTextPrimary--text mb-1">
                {{ $t("message.admin_plus_upgrade_plan_coral") }}
              </div>
              <div class="d-flex align-baseline flex-wrap mb-1">
                <span class="font-roboto text-h3 font-weight-bold plusTextPrimary--text">${{ billingYearly ? "1,610.00"
                  :
                  displayPriceCoral }}</span>

              </div>
              <div class="font-roboto text-body-2 plusTextSecondary--text mb-3">
                {{ coralBillingNote }}
              </div>
              <div class="font-roboto text-body-2 plusGreenAccent4--text mb-4 d-flex align-center">
                <v-icon small color="plusGreenAccent4" class="mr-1">mdi-tag-outline</v-icon>
                {{ $t("message.admin_plus_upgrade_save_annual_line") }}
              </div>
              <ul class="feature-list pl-0 mb-6">
                <li v-for="key in featureKeys" :key="'c-' + key" class="d-flex align-center justify-start  mb-2">
                  <v-icon color="plusGreenAccent4" size="20" class="mr-2 mt-0">mdi-check-circle</v-icon>
                  <span class="font-roboto text-body-1 plusTextPrimary--text">{{ $t(key) }}</span>
                </li>
              </ul>
              <v-btn block x-large outlined color="plusTextPrimary"
                class="font-roboto font-weight-medium mt-auto text-uppercase" :disabled="checkoutDisabled"
                :loading="checkoutLoading" @click="onSelectPlan('coral')">
                {{ $t("message.admin_plus_upgrade_cta_coral") }}
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import Api from "@/services/Api";
import { Environment } from "@/services/Environment";

const BLACK_MONTHLY = 49;
const CORAL_MONTHLY = 149;
const YEARLY_DISCOUNT = 0.9;

export default {
  name: "ViewUpgradePlanAdminPlus",
  data() {
    return {
      checkoutLoading: false,
      billingYearly: true,
      featureKeys: [
        "message.admin_plus_upgrade_feature_unlimited_students",
        "message.admin_plus_upgrade_feature_payments",
        "message.admin_plus_upgrade_feature_attendance",
        "message.admin_plus_upgrade_feature_student_data",
        "message.admin_plus_upgrade_feature_automations",
        "message.admin_plus_upgrade_feature_engagement",
        "message.admin_plus_upgrade_feature_lms",
        "message.admin_plus_upgrade_feature_marketing",
        "message.admin_plus_upgrade_feature_leads",
      ],
      fetchedStripeSubscription: null,
      subscriptionFetchLoading: false,
      subscriptionFetchError: null,
    };
  },
  async mounted() {
    await this.handleStripeReturn();
    await this.refreshAcademyAdminStatus();
    await this.loadSubscriptionDetails();
  },
  computed: {
    isTrial() {
      return this.$store.state.academyAdminStatus?.isTrial === true;
    },
    showSubscriptionDetails() {
      const isUserAdminPlus = this.$store.state.academyAdminStatus?.settings?.isUserAdminPlus;
      const statusConsideredActive = ["active", "trialing"];

      const statusStripe = this.$store.state.academyAdminStatus?.activeSubscription?.stripe?.status || "invalid";
      return isUserAdminPlus && (this.isTrial || !statusConsideredActive.includes(statusStripe));
    },
    headerTitle() {
      return this.isTrial
        ? this.$t("message.admin_plus_upgrade_now")
        : this.$t("message.admin_plus_subscription_title");
    },
    stripePayload() {
      const s = this.$store.state.academyAdminStatus?.activeSubscription?.stripe;
      return s && typeof s === "object" ? s : null;
    },
    stripeSubscriptionId() {
      const s = this.stripePayload;
      if (!s) return null;
      const raw = s.subscriptionId ?? s.subscription_id;
      if (raw === undefined || raw === null || raw === "") return null;
      return String(raw);
    },
    mergedStripeInput() {
      const store = this.stripePayload;

      const fetched = this.fetchedStripeSubscription;
      if (!fetched) return store;

      const storeSafe = store && typeof store === "object" ? { ...store } : {};

      const isRootStripeSubscription =
        fetched.object === "subscription" ||
        (typeof fetched.id === "string" && fetched.id.indexOf("sub_") === 0);

      if (isRootStripeSubscription) {
        return {
          ...storeSafe,
          subscription: fetched,
          default_payment_method:
            storeSafe.default_payment_method ||
            storeSafe.paymentMethod ||
            fetched.default_payment_method,
          invoices:
            fetched.invoices ||
            fetched.recentInvoices ||
            storeSafe.invoices ||
            storeSafe.recentInvoices,
          recentInvoices:
            fetched.recentInvoices ||
            storeSafe.recentInvoices,
          managePlanUrl: storeSafe.managePlanUrl || storeSafe.manage_plan_url || fetched.managePlanUrl,
          manage_plan_url: storeSafe.manage_plan_url || fetched.manage_plan_url,
          manageBillingUrl: storeSafe.manageBillingUrl || storeSafe.manage_billing_url || fetched.manageBillingUrl,
          manage_billing_url: storeSafe.manage_billing_url || fetched.manage_billing_url,
          customerPortalUrl: storeSafe.customerPortalUrl || storeSafe.customer_portal_url || fetched.customerPortalUrl,
          billingPortalUrl: storeSafe.billingPortalUrl || storeSafe.billing_portal_url || fetched.billingPortalUrl,
        };
      }

      const nestedSub = fetched.subscription || fetched.data;
      return {
        ...storeSafe,
        ...fetched,
        subscription: nestedSub || storeSafe.subscription,
        invoices:
          fetched.invoices ||
          fetched.recentInvoices ||
          storeSafe.invoices ||
          storeSafe.recentInvoices,
        default_payment_method:
          fetched.default_payment_method ||
          storeSafe.default_payment_method ||
          nestedSub?.default_payment_method,
        managePlanUrl: fetched.managePlanUrl || storeSafe.managePlanUrl || storeSafe.manage_plan_url,
        manageBillingUrl: fetched.manageBillingUrl || storeSafe.manageBillingUrl || storeSafe.manage_billing_url,
        customerPortalUrl: fetched.customerPortalUrl || storeSafe.customerPortalUrl,
        billingPortalUrl: fetched.billingPortalUrl || storeSafe.billingPortalUrl,
      };
    },
    subscriptionViewModel() {
      return this.buildSubscriptionViewModel(this.mergedStripeInput);
    },

    subscriptionDetailRows() {
      const vm = this.subscriptionViewModel;
      return [
        {
          key: "plan",
          label: this.$t("message.admin_plus_subscription_plan_label"),
          value: vm.planName,
        },
        {
          key: "price",
          label: this.$t("message.admin_plus_subscription_price_label"),
          value: vm.priceRowDisplay,
        },
        {
          key: "cycle",
          label: this.$t("message.admin_plus_subscription_billing_cycle_label"),
          value: vm.billingCycleLabel,
        },
        {
          key: "next",
          label: this.$t("message.admin_plus_subscription_next_payment_label"),
          value: vm.nextPaymentFormatted,
        },
        {
          key: "pm",
          label: this.$t("message.admin_plus_subscription_payment_method_label"),
          value: vm.paymentMethodValue,
          paymentBrand: vm.paymentBrandDisplay,
        },
        {
          key: "fee",
          label: this.$t("message.admin_plus_subscription_service_fee_label"),
          value: vm.serviceFeeDisplay,
        },
      ];
    },
    trialEndDate() {
      const raw = this.$store.state.academyAdminStatus?.subscriptionEndsAt;
      if (!raw) return null;
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return null;
      return d;
    },
    trialEndFormatted() {
      if (!this.trialEndDate) return null;
      try {
        return this.$d(this.trialEndDate, "month_day_year", this.$i18n.locale);
      } catch {
        return null;
      }
    },
    planSubtitle() {
      if (this.isTrial && this.trialEndFormatted) {
        return this.$t("message.admin_plus_upgrade_subtitle_with_date", {
          date: this.trialEndFormatted,
        });
      }
      return this.$t("message.admin_plus_upgrade_subtitle_fallback");
    },
    hasConfiguredStripePrices() {
      return (
        !!this.stripePriceBlackMonthly &&
        !!this.stripePriceBlackYearly &&
        !!this.stripePriceCoralMonthly &&
        !!this.stripePriceCoralYearly
      );
    },
    stripePriceBlackMonthly() {
      return (Environment.get("VUE_APP_STRIPE_PRICE_ADMIN_PLUS_BLACK_MONTHLY") || "").trim();
    },
    stripePriceBlackYearly() {
      return (Environment.get("VUE_APP_STRIPE_PRICE_ADMIN_PLUS_BLACK_YEARLY") || "").trim();
    },
    stripePriceCoralMonthly() {
      return (Environment.get("VUE_APP_STRIPE_PRICE_ADMIN_PLUS_CORAL_MONTHLY") || "").trim();
    },
    stripePriceCoralYearly() {
      return (Environment.get("VUE_APP_STRIPE_PRICE_ADMIN_PLUS_CORAL_YEARLY") || "").trim();
    },
    checkoutDisabled() {
      return (
        this.checkoutLoading ||
        !this.hasConfiguredStripePrices ||
        !this.$store.state.managedAcademy
      );
    },
    displayPriceBlack() {
      const n = this.billingYearly
        ? Math.round(BLACK_MONTHLY * YEARLY_DISCOUNT)
        : BLACK_MONTHLY;
      return n;
    },
    displayPriceCoral() {
      const n = this.billingYearly
        ? Math.round(CORAL_MONTHLY * YEARLY_DISCOUNT)
        : CORAL_MONTHLY;
      return n;
    },
    blackBillingNote() {
      return this.billingYearly
        ? this.$t("message.admin_plus_upgrade_black_billed_yearly")
        : this.$t("message.admin_plus_upgrade_black_billed_monthly");
    },
    coralBillingNote() {
      return this.billingYearly
        ? this.$t("message.admin_plus_upgrade_coral_billed_yearly")
        : this.$t("message.admin_plus_upgrade_coral_billed_monthly");
    },
  },
  methods: {
    buildSubscriptionViewModel(s) {
      const missing = "";
      const empty = {
        planName: missing,
        priceRowDisplay: missing,
        billingCycleLabel: missing,
        nextPaymentFormatted: missing,
        paymentMethodValue: missing,
        paymentBrandDisplay: "",
        serviceFeeDisplay: missing,
        barRest: "",
        priceWithIntervalForBar: "",
        invoices: [],
        managePlanUrl: null,
        manageBillingUrl: null,
      };
      if (!s) return empty;

      if (s.plan && typeof s.plan === "object" && s.plan.name != null) {
        return this.buildSubscriptionViewModelFromAdminPlusDto(s);
      }

      const subscription = s.subscription && typeof s.subscription === "object" ? s.subscription : null;
      const root = subscription || s;

      const periodEndRaw = root.current_period_end ?? s.current_period_end;
      let nextPaymentDate = null;
      if (periodEndRaw != null && periodEndRaw !== "") {
        const n = Number(periodEndRaw);
        const ms = !Number.isNaN(n) && n < 1e12 ? n * 1000 : n;
        const d = new Date(ms);
        if (!Number.isNaN(d.getTime())) nextPaymentDate = d;
      }

      let itemsData = subscription?.items?.data;
      if (!itemsData && Array.isArray(s.items)) itemsData = s.items;
      const firstItem = itemsData?.[0];
      const priceObj = firstItem?.price || firstItem?.plan || s.price || {};
      const recurring = priceObj.recurring || {};
      const interval = recurring.interval || s.interval || priceObj.interval;
      const intervalCount = recurring.interval_count ?? s.interval_count ?? priceObj.interval_count ?? 1;

      let unitAmount = priceObj.unit_amount;
      if (unitAmount == null && firstItem?.plan?.amount_decimal != null) {
        unitAmount = Math.round(Number(firstItem.plan.amount_decimal));
      }
      if (unitAmount == null && firstItem?.plan?.amount != null) unitAmount = firstItem.plan.amount;
      if (unitAmount == null) unitAmount = s.unit_amount;

      const currency = (priceObj.currency || s.currency || "usd").toString().toLowerCase();

      let planName =
        s.productName ||
        s.planName ||
        priceObj.nickname ||
        s.nickname;
      if (!planName && priceObj.product) {
        if (typeof priceObj.product === "object" && priceObj.product.name) {
          planName = priceObj.product.name;
        }
      }
      if (!planName) planName = missing;

      const serviceFeeDisplay = this.resolveServiceFeeDisplay(s, planName);

      const pm = s.default_payment_method || s.paymentMethod || root.default_payment_method;
      let paymentMethodValue = missing;
      let paymentBrandDisplay = "";
      if (pm && typeof pm === "object") {
        const card = pm.card || pm;
        const brandRaw = (card.brand || card.display_brand || "").toString();
        const last4 = card.last4 || card.last_four;
        if (last4) {
          paymentBrandDisplay = brandRaw
            ? brandRaw.charAt(0).toUpperCase() + brandRaw.slice(1).toLowerCase()
            : "";
          paymentMethodValue = this.$t("message.admin_plus_subscription_payment_method_ending", {
            last4: String(last4),
          });
        }
      }

      const amountFormatted =
        unitAmount != null && !Number.isNaN(Number(unitAmount))
          ? this.formatStripeAmount(Number(unitAmount), currency)
          : missing;

      const intervalSuffix = this.intervalSuffix(interval, intervalCount);
      const priceWithIntervalForBar =
        amountFormatted !== missing && interval
          ? `${amountFormatted}${intervalSuffix}`
          : amountFormatted;

      const billingCycleLabel = this.billingCycleLabel(interval, intervalCount);

      const priceRowDisplay =
        amountFormatted !== missing && interval
          ? `${amountFormatted}${intervalSuffix}`
          : amountFormatted;

      let barRest = "";
      if (priceWithIntervalForBar !== missing) {
        barRest += ` ${priceWithIntervalForBar}`;
      }
      if (serviceFeeDisplay !== missing) {
        barRest += this.$t("message.admin_plus_subscription_bar_comma_plus_fee", {
          fee: serviceFeeDisplay,
        });
      }

      let invList = s.invoices;
      if (!Array.isArray(invList) && Array.isArray(s.recentInvoices)) invList = s.recentInvoices;
      const invoices = (invList || []).slice(0, 20).map((inv, idx) => this.normalizeInvoiceRow(inv, currency, idx));

      const managePlanUrl =
        s.managePlanUrl || s.manage_plan_url || s.customerPortalUrl || s.customer_portal_url || null;
      const manageBillingUrl =
        s.manageBillingUrl || s.manage_billing_url || s.billingPortalUrl || s.billing_portal_url || null;

      return {
        planName,
        priceRowDisplay,
        billingCycleLabel,
        nextPaymentFormatted: nextPaymentDate
          ? this.formatSubscriptionDate(nextPaymentDate)
          : missing,
        paymentMethodValue,
        paymentBrandDisplay,
        serviceFeeDisplay,
        barRest,
        priceWithIntervalForBar,
        invoices,
        managePlanUrl: managePlanUrl || null,
        manageBillingUrl: manageBillingUrl || managePlanUrl || null,
      };
    },
    /**
     * Backend DTO from GET trial/admin-plus/subscription/:id
     * (plan, nextBilling, paymentMethod, serviceFee, subscription, invoices).
     */
    buildSubscriptionViewModelFromAdminPlusDto(s) {
      const missing = "";
      const plan = s.plan;
      const currency = (plan.currency || "usd").toString().toLowerCase();

      const rawPlanTitle = String(plan.name || "").trim();
      const planName =
        rawPlanTitle.replace(/^[\s\S]*Admin\+\s*/i, "").trim() || rawPlanTitle || missing;

      const priceRowDisplay =
        plan.formatted != null && String(plan.formatted).trim() !== ""
          ? String(plan.formatted)
          : plan.unitAmount != null && !Number.isNaN(Number(plan.unitAmount))
            ? this.formatCurrencyMajor(Number(plan.unitAmount), currency) +
            this.intervalSuffix(plan.interval, plan.intervalCount || 1)
            : missing;

      const billingCycleLabel =
        plan.billingCycleLabel != null && String(plan.billingCycleLabel).trim() !== ""
          ? String(plan.billingCycleLabel)
          : this.billingCycleLabel(plan.interval, plan.intervalCount || 1);

      const nb = s.nextBilling && typeof s.nextBilling === "object" ? s.nextBilling : {};
      let nextPaymentFormatted = missing;
      if (nb.label != null && String(nb.label).trim() !== "") {
        nextPaymentFormatted = String(nb.label);
      } else if (nb.iso != null && String(nb.iso).trim() !== "") {
        const d = new Date(nb.iso);
        if (!Number.isNaN(d.getTime())) nextPaymentFormatted = this.formatSubscriptionDate(d);
      } else if (nb.unix != null && nb.unix !== "") {
        const n = Number(nb.unix);
        const ms = !Number.isNaN(n) && n < 1e12 ? n * 1000 : n;
        const d = new Date(ms);
        if (!Number.isNaN(d.getTime())) nextPaymentFormatted = this.formatSubscriptionDate(d);
      }

      let paymentMethodValue = missing;
      let paymentBrandDisplay = "";
      const pm = s.paymentMethod && typeof s.paymentMethod === "object" ? s.paymentMethod : null;
      if (pm) {
        if (pm.display != null && String(pm.display).trim() !== "") {
          paymentMethodValue = String(pm.display);
          paymentBrandDisplay = "";
        } else if (pm.last4) {
          const brandRaw = (pm.brand || "").toString();
          paymentBrandDisplay = brandRaw
            ? brandRaw.charAt(0).toUpperCase() + brandRaw.slice(1).toLowerCase()
            : "";
          paymentMethodValue = this.$t("message.admin_plus_subscription_payment_method_ending", {
            last4: String(pm.last4),
          });
        }
      }

      const serviceFeeDisplay = this.resolveServiceFeeDisplay(s, planName);

      const priceWithIntervalForBar = priceRowDisplay !== missing ? priceRowDisplay : missing;
      let barRest = "";
      if (priceWithIntervalForBar !== missing) {
        barRest += ` ${priceWithIntervalForBar}`;
      }
      if (serviceFeeDisplay !== missing) {
        barRest += this.$t("message.admin_plus_subscription_bar_comma_plus_fee", {
          fee: serviceFeeDisplay,
        });
      }

      let invList = s.invoices;
      if (!Array.isArray(invList)) invList = [];
      const invoices = invList.slice(0, 20).map((inv, idx) =>
        this.normalizeInvoiceRow(inv, currency, idx),
      );

      const managePlanUrl =
        s.managePlanUrl || s.manage_plan_url || s.customerPortalUrl || s.customer_portal_url || null;
      const manageBillingUrl =
        s.manageBillingUrl || s.manage_billing_url || s.billingPortalUrl || s.billing_portal_url || null;

      return {
        planName,
        priceRowDisplay,
        billingCycleLabel,
        nextPaymentFormatted,
        paymentMethodValue,
        paymentBrandDisplay,
        serviceFeeDisplay,
        barRest,
        priceWithIntervalForBar,
        invoices,
        managePlanUrl: managePlanUrl || null,
        manageBillingUrl: manageBillingUrl || managePlanUrl || null,
      };
    },
    formatCurrencyMajor(amount, currencyCode) {
      const cur = (currencyCode || "usd").toString().toUpperCase();
      const n = Number(amount);
      if (Number.isNaN(n)) return "";
      try {
        return new Intl.NumberFormat(this.$i18n.locale || "en", {
          style: "currency",
          currency: cur,
        }).format(n);
      } catch {
        return `$${n.toFixed(2)}`;
      }
    },
    resolveServiceFeeDisplay(s, planName) {
      const missing = "";
      if (s.serviceFee && typeof s.serviceFee === "object") {
        const sf = s.serviceFee;
        if (sf.label != null && String(sf.label).trim() !== "") {
          return String(sf.label);
        }
        if (typeof sf.percent === "number") {
          return this.$t("message.admin_plus_subscription_service_fee_percent", { n: sf.percent });
        }
      }
      if (s.serviceFeeLabel != null && String(s.serviceFeeLabel).trim() !== "") {
        return String(s.serviceFeeLabel);
      }
      if (typeof s.serviceFeePercent === "number") {
        return this.$t("message.admin_plus_subscription_service_fee_percent", { n: s.serviceFeePercent });
      }
      const slug = `${s.planSlug || ""} ${planName || ""}`.toLowerCase();
      if (slug.includes("black")) {
        return this.$t("message.admin_plus_subscription_service_fee_black");
      }
      if (slug.includes("coral")) {
        return this.$t("message.admin_plus_subscription_service_fee_coral");
      }
      return missing;
    },
    formatStripeAmount(cents, currencyCode) {
      const cur = (currencyCode || "usd").toString().toUpperCase();
      const n = Number(cents) / 100;
      if (Number.isNaN(n)) return "";
      try {
        return new Intl.NumberFormat(this.$i18n.locale || "en", {
          style: "currency",
          currency: cur,
        }).format(n);
      } catch {
        return `$${n.toFixed(2)}`;
      }
    },
    formatSubscriptionDate(d) {
      try {
        return this.$d(d, "month_day_year", this.$i18n.locale);
      } catch {
        return "";
      }
    },
    intervalSuffix(interval, intervalCount) {
      if (!interval) return "";
      const ic = Number(intervalCount) || 1;
      if (interval === "month" && ic === 1) return this.$t("message.admin_plus_subscription_per_month");
      if (interval === "year" && ic === 1) return this.$t("message.admin_plus_subscription_per_year");
      if (interval === "month" && ic > 1) {
        return this.$t("message.admin_plus_subscription_per_n_months", { n: ic });
      }
      return "";
    },
    billingCycleLabel(interval, intervalCount) {
      const missing = "";
      if (!interval) return missing;
      const ic = Number(intervalCount) || 1;
      if (interval === "year" && ic === 1) return this.$t("message.admin_plus_subscription_billing_yearly");
      if (interval === "month" && ic === 1) return this.$t("message.admin_plus_subscription_billing_monthly");
      if (interval === "month" && ic > 1) {
        return this.$t("message.admin_plus_subscription_billing_every_n_months", { n: ic });
      }
      return interval;
    },
    normalizeInvoiceRow(inv, defaultCurrency, idx) {
      const missing = "";
      const created = inv.created ?? inv.date;
      let dateFormatted = missing;
      if (created != null) {
        if (typeof created === "string" && Number.isNaN(Number(created))) {
          const d = new Date(created);
          if (!Number.isNaN(d.getTime())) dateFormatted = this.formatSubscriptionDate(d);
        } else {
          const n = Number(created);
          const ms = !Number.isNaN(n) && n < 1e12 ? n * 1000 : n;
          const d = new Date(ms);
          if (!Number.isNaN(d.getTime())) dateFormatted = this.formatSubscriptionDate(d);
        }
      }
      const num = inv.number || inv.id;
      const numberDisplay = num != null ? (String(num).startsWith("#") ? String(num) : `#${num}`) : missing;

      const currency = (inv.currency || defaultCurrency || "usd").toString().toLowerCase();
      const totalCents = inv.total ?? inv.amount_paid ?? inv.amount_due;
      let amountFormatted = missing;
      if (totalCents != null && !Number.isNaN(Number(totalCents))) {
        amountFormatted = this.formatStripeAmount(Number(totalCents), currency);
      } else if (
        inv.amount != null &&
        !Number.isNaN(Number(inv.amount)) &&
        (inv.invoicePdf != null || inv.invoice_pdf != null)
      ) {
        amountFormatted = this.formatCurrencyMajor(Number(inv.amount), currency);
      } else if (inv.amount != null && !Number.isNaN(Number(inv.amount))) {
        amountFormatted = this.formatCurrencyMajor(Number(inv.amount), currency);
      }

      const statusRaw = (inv.status || "").toString().toLowerCase();
      const statusFormatted = statusRaw
        ? this.formatInvoiceStatus(statusRaw)
        : this.$t("message.admin_plus_subscription_status_unknown");

      let statusChip = "";
      let statusChipColor = "";
      if (statusRaw === "paid") {
        statusChip = this.$t("message.paid");
        statusChipColor = "plusGreen";
      }

      const pdf = inv.invoice_pdf || inv.invoicePdf;
      const hosted = inv.hosted_invoice_url || inv.hostedInvoiceUrl;
      let downloadUrl = pdf || hosted || null;
      const downloadChoices = [];
      if (pdf) downloadChoices.push({ label: "PDF", url: pdf });
      if (hosted && hosted !== pdf) downloadChoices.push({ label: this.$t("message.admin_plus_subscription_invoice_hosted"), url: hosted });

      const idKey = inv.id || inv.number || `inv-${idx}`;

      return {
        idKey,
        dateFormatted,
        numberDisplay,
        amountFormatted,
        statusRaw,
        statusFormatted,
        statusChip,
        statusChipColor,
        downloadUrl,
        downloadChoices: downloadChoices.length > 1 ? downloadChoices : null,
      };
    },
    formatInvoiceStatus(statusRaw) {
      const map = {
        paid: "message.paid",
        open: "message.admin_plus_subscription_invoice_status_open",
        void: "message.admin_plus_subscription_invoice_status_void",
        uncollectible: "message.admin_plus_subscription_invoice_status_uncollectible",
        draft: "message.admin_plus_subscription_invoice_status_draft",
      };
      const key = map[statusRaw];
      if (key) {
        try {
          return this.$t(key);
        } catch {
          return statusRaw;
        }
      }
      return statusRaw.replace(/_/g, " ");
    },
    openInvoiceUrl(url) {
      if (!url) {
        this.showInfoAlert(this.$t("message.admin_plus_subscription_invoice_no_download"));
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    },
    onInvoiceDownload(inv) {
      if (inv.downloadUrl) {
        window.open(inv.downloadUrl, "_blank", "noopener,noreferrer");
        return;
      }
      this.showInfoAlert(this.$t("message.admin_plus_subscription_invoice_no_download"));
    },
    /** @param {'success' | 'info' | 'error'} type */
    getAlertTitle(type) {
      if (type === "success") return this.$t("message.success");
      if (type === "info") return this.$t("message.info");
      return this.$t("message.error");
    },
    /**
     * @param {'success' | 'info' | 'error'} type
     * @param {any} text
     */
    fireAlert(type, text) {
      /** @type {any} */
      const vm = this;
      vm.showAlert(
        type,
        this.getAlertTitle(type),
        String(text),
        10000,
        "bottom",
      );
    },
    /** @param {any} text */
    showInfoAlert(text) {
      this.fireAlert("info", text);
    },
    onManagePlan() {
      const url = this.subscriptionViewModel.managePlanUrl;
      if (url) {
        window.location.assign(url);
        return;
      }
      console.warn("[Admin+] Manage plan: set academyAdminStatus.stripe.managePlanUrl when API is ready.");
      this.showInfoAlert(this.$t("message.admin_plus_subscription_billing_unavailable"));
    },
    onManageBilling() {
      const url = this.subscriptionViewModel.manageBillingUrl;
      if (url) {
        window.location.assign(url);
        return;
      }
      console.warn("[Admin+] Manage billing: set academyAdminStatus.stripe.manageBillingUrl when API is ready.");
      this.showInfoAlert(this.$t("message.admin_plus_subscription_billing_unavailable"));
    },
    onViewAllInvoices() {
      console.warn("[Admin+] View all invoices: add route or URL when available.");
      // this.showInfoAlert(this.$t("message.admin_plus_subscription_billing_unavailable"));
    },
    /** @param {any} value */
    isTruthyQueryParam(value) {
      if (value === undefined || value === null) return false;
      if (value === true) return true;
      const s = String(value).trim().toLowerCase();
      return s === "1" || s === "true" || s === "yes" || s === "ok";
    },
    async refreshAcademyAdminStatus() {
      const managedAcademy = this.$store.state.managedAcademy;
      if (!managedAcademy) return;
      try {
        const response = await Api().get(
          `admins/v3/managedacademy/${managedAcademy}/get-academy-admin-status`,
        );
        if (response?.data) {
          this.$store.dispatch("setAcademyAdminStatus", response.data);
        }
      } catch (e) {
        console.error("Refresh academy admin status:", e);
      }
    },
    async loadSubscriptionDetails() {
      if (this.isTrial || this.showSubscriptionDetails) return;
      const id = this.stripeSubscriptionId;
      console.log('loadSubscriptionDetails', id);
      if (!id) return;

      this.subscriptionFetchLoading = true;
      this.subscriptionFetchError = null;
      try {
        const response = await Api().get(
          `trial/admin-plus/subscription/${encodeURIComponent(id)}`,
        );
        console.log('response', response);
        this.fetchedStripeSubscription = response?.data ?? null;
      } catch (e) {
        this.subscriptionFetchError = e;

        console.log("Load Stripe subscription:", e);
      } finally {
        this.subscriptionFetchLoading = false;
      }
    },
    async clearStripeReturnQueryParams() {
      const q = { ...this.$route.query };
      delete q.success;
      delete q.canceled;
      delete q.cancelled;
      delete q.session_id;
      await this.$router.replace({
        name: "upgrade-plan-adminplus",
        query: q,
      });
    },
    async handleStripeReturn() {
      const q = this.$route.query || {};
      const isSuccess = this.isTruthyQueryParam(q.success);
      const isCanceled =
        this.isTruthyQueryParam(q.canceled) || this.isTruthyQueryParam(q.cancelled);

      if (!isSuccess && !isCanceled) return;

      if (isSuccess) {
        this.fireAlert(
          "success",
          this.$t("message.admin_plus_upgrade_snackbar_success"),
        );
      } else {
        this.fireAlert(
          "info",
          this.$t("message.admin_plus_upgrade_snackbar_cancel"),
        );
      }

      await this.refreshAcademyAdminStatus();
      await this.clearStripeReturnQueryParams();
    },
    /** @param {'black' | 'coral'} plan */
    stripePriceIdForPlan(plan) {
      const yearly = this.billingYearly;
      if (plan === "black") {
        return yearly
          ? this.stripePriceBlackYearly
          : this.stripePriceBlackMonthly;
      }
      if (plan === "coral") {
        return yearly
          ? this.stripePriceCoralYearly
          : this.stripePriceCoralMonthly;
      }
      return "";
    },
    /** @param {'black' | 'coral'} plan */
    async onSelectPlan(plan) {
      if (this.checkoutDisabled) return;

      const managedAcademyId = this.$store.state.managedAcademy;
      const priceId = this.stripePriceIdForPlan(plan);
      if (!managedAcademyId || !priceId) {
        this.fireAlert("error", this.$t("message.admin_plus_upgrade_config_error"));
        return;
      }

      this.checkoutLoading = true;
      try {
        const { data } = await Api().post("trial/admin-plus/checkout-session", {
          managedAcademyId,
          priceId,
        });

        if (data && data.url) {
          window.location.assign(data.url);
          return;
        }
        this.fireAlert("error", this.$t("message.admin_plus_upgrade_checkout_error"));
      } catch (e) {
        /** @type {any} */
        const err = e;
        const msg =
          err?.response?.data?.message ||
          String(this.$t("message.admin_plus_upgrade_checkout_error"));
        this.fireAlert("error", msg);
      } finally {
        this.checkoutLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.header-container {
  border-bottom: 1px solid var(--v-plusBorder-base);
}

.upgrade-plan-page {
  min-height: 100%;
  background-color: var(--v-plusBackground-base, #fafafa);
}

.billing-pill {
  border: 1px solid var(--v-plusBorder-base, #e0e0e0);
  overflow: hidden;
  background: var(--v-plusSurface-base, #fff);
  box-shadow: 0 10px 20px 0 rgba(13, 13, 13, 0.15);
}

.billing-pill-btn {
  text-transform: none;
  letter-spacing: normal;
  min-height: 44px !important;
  color: #757575 !important;
  background-color: transparent !important;
  box-shadow: none !important;
}

.billing-pill-btn--active {
  background-color: var(--v-plusGreyDarken3-base, #383838) !important;
  color: #fff !important;
}

.plan-card {
  border-radius: 12px;
}

.plan-card--black {
  position: relative;
  overflow: visible;
}

.plan-card--coral {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.10), 0 4px 6px -4px rgba(0, 0, 0, 0.10);
  border: 1px solid var(--v-plusBorder-base) !important;
}

.plan-badges {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 10px 15px 15px rgba(0, 0, 0, 0.10), 0 4px 6px -4px rgba(0, 0, 0, 0.10);

}

.feature-list {
  list-style: none;
}

.cta-black-plan {
  color: #000 !important;
}

.sheet-banner {
  background: linear-gradient(90deg, #B9F6CA 0%, #FFF 100%);
}

.theme--dark .sheet-banner {
  background: linear-gradient(90deg, rgba(0, 200, 83, 0.12) 0%, transparent 100%) !important;
  border: 1px solid rgba(0, 200, 83, 0.2);
}

.subscription-current-card {
  border: 1px solid var(--v-plusBorder-base) !important;
}

.subscription-plan-bar {
  border-radius: 0;
}

.subscription-detail-row {
  border-bottom: 1px solid var(--v-plusBorder-base);
}

.subscription-detail-row:last-of-type {
  border-bottom: none;
}

.subscription-payment-brand {
  font-weight: 600;
}

.subscription-invoices-card {
  border: 1px solid var(--v-plusBorder-base) !important;
}
</style>